import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Terms of Service</h1>
            <p className="text-gray-600 mb-8">Last updated: January 2025</p>

            <div className="prose prose-lg max-w-none space-y-8">
              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-600">
                  By accessing and using FourQ Learn's services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform. For users under 18, parental or guardian consent is required.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Service Description</h2>
                <p className="text-gray-600 mb-4">
                  FourQ Learn provides online 1-on-1 tutoring services for students in grades 3-12. Our services include:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Live video tutoring sessions</li>
                  <li>Progress tracking and reporting</li>
                  <li>Learning materials and resources</li>
                  <li>Session recordings (where applicable)</li>
                  <li>AI-powered practice tools</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
                <p className="text-gray-600 mb-4">
                  To use our services, you must:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Provide accurate and complete registration information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized account access</li>
                  <li>Be responsible for all activities under your account</li>
                  <li>Not share your account with others</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Payment and Billing</h2>
                <p className="text-gray-600 mb-4">
                  Regarding payments:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>All fees are in Indian Rupees (INR) unless otherwise stated</li>
                  <li>Payment is required before sessions are scheduled</li>
                  <li>Monthly subscriptions auto-renew unless cancelled</li>
                  <li>Refunds are provided according to our refund policy</li>
                  <li>We reserve the right to change pricing with 30 days notice</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Session Policies</h2>
                <p className="text-gray-600 mb-4">
                  For tutoring sessions:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Students must join sessions on time; late arrivals cannot extend session duration</li>
                  <li>Cancellations must be made at least 24 hours in advance for a full credit</li>
                  <li>No-shows without notice will not be refunded</li>
                  <li>Technical issues: If caused by our platform, sessions will be rescheduled at no charge</li>
                  <li>Students are expected to maintain respectful behavior during sessions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Acceptable Use</h2>
                <p className="text-gray-600 mb-4">
                  Users agree NOT to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Use the service for any illegal or unauthorized purpose</li>
                  <li>Harass, abuse, or harm tutors or other users</li>
                  <li>Share, distribute, or record sessions without permission</li>
                  <li>Attempt to access unauthorized areas of the platform</li>
                  <li>Upload malicious code or interfere with platform operations</li>
                  <li>Impersonate any person or entity</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
                <p className="text-gray-600">
                  All content, materials, and resources provided through FourQ Learn are protected by copyright and other intellectual property laws. Users may not reproduce, distribute, or create derivative works without explicit permission.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Termination</h2>
                <p className="text-gray-600">
                  We reserve the right to suspend or terminate accounts that violate these terms or engage in inappropriate behavior. Users may cancel their accounts at any time through their account settings or by contacting support.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Disclaimers</h2>
                <p className="text-gray-600">
                  FourQ Learn provides services "as is" without warranties. While we strive for excellence, we do not guarantee specific learning outcomes. Results depend on student effort, engagement, and individual circumstances.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
                <p className="text-gray-600">
                  FourQ Learn shall not be liable for indirect, incidental, or consequential damages arising from use of our services. Our total liability is limited to the amount paid by you in the past 6 months.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
                <p className="text-gray-600">
                  We may modify these Terms of Service at any time. Significant changes will be communicated via email or platform notification. Continued use after changes constitutes acceptance of new terms.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
                <p className="text-gray-600 mb-4">
                  For questions about these Terms of Service:
                </p>
                <div className="bg-cyan-50 rounded-xl p-6">
                  <p className="text-gray-900 font-semibold">Email: contact@fourqlearn.com</p>
                  <p className="text-gray-900 font-semibold">Phone: +91 9538667790</p>
                  <p className="text-gray-900 font-semibold">Address: I-704, Smondo 3.0, Neotown, Electronics City Phase 1, Bangalore-560100</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;